/**
 * Fullscreen helpers for the web player (Sentry PLAYER-11).
 * iOS Safari / Chrome on iOS often omit the unprefixed Fullscreen API; feature-detect
 * (including webkit) and no-op instead of throwing.
 */

type RequestFullscreenFn = (options?: FullscreenOptions) => Promise<void>
type ExitFullscreenFn = () => Promise<void>

type FullscreenCapableElement = Element & {
  requestFullscreen?: RequestFullscreenFn
  webkitRequestFullscreen?: RequestFullscreenFn
  webkitRequestFullScreen?: RequestFullscreenFn
  msRequestFullscreen?: RequestFullscreenFn
}

type FullscreenCapableDocument = Document & {
  exitFullscreen?: ExitFullscreenFn
  webkitExitFullscreen?: ExitFullscreenFn
  webkitCancelFullScreen?: ExitFullscreenFn
  msExitFullscreen?: ExitFullscreenFn
  webkitFullscreenElement?: Element | null
  msFullscreenElement?: Element | null
}

function asFullscreenElement(el: Element | null | undefined): FullscreenCapableElement | null {
  return el != null ? (el as FullscreenCapableElement) : null
}

export function getFullscreenElement(doc: Document = document): Element | null {
  const d = doc as FullscreenCapableDocument
  return d.fullscreenElement ?? d.webkitFullscreenElement ?? d.msFullscreenElement ?? null
}

export function resolveRequestFullscreen(el: Element | null | undefined): RequestFullscreenFn | null {
  const node = asFullscreenElement(el)
  if (node == null) { return null }
  const fn = node.requestFullscreen
    ?? node.webkitRequestFullscreen
    ?? node.webkitRequestFullScreen
    ?? node.msRequestFullscreen
  return typeof fn === 'function' ? fn.bind(node) : null
}

export function resolveExitFullscreen(doc: Document = document): ExitFullscreenFn | null {
  const d = doc as FullscreenCapableDocument
  const fn = d.exitFullscreen
    ?? d.webkitExitFullscreen
    ?? d.webkitCancelFullScreen
    ?? d.msExitFullscreen
  return typeof fn === 'function' ? fn.bind(d) : null
}

/** True when the page can enter or leave fullscreen via a known vendor API. */
export function isWebFullscreenApiAvailable(doc: Document = document): boolean {
  if (typeof document === 'undefined') { return false }
  return resolveRequestFullscreen(doc.documentElement) != null
    || resolveExitFullscreen(doc) != null
}

export async function requestWebFullscreen(el?: Element | null): Promise<void> {
  const target = el ?? (typeof document !== 'undefined' ? document.documentElement : null)
  const request = resolveRequestFullscreen(target)
  if (request == null) { return }
  await request().catch(() => {})
}

export async function exitWebFullscreen(doc: Document = document): Promise<void> {
  const exit = resolveExitFullscreen(doc)
  if (exit == null) { return }
  await exit().catch(() => {})
}
